import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RIGHT_SIDEBAR_ACTION } from '@@types/index';
import { adminWorkspaceAtom, adminTablesAtom } from '@jotai/admin/atoms';
import { externalSidebarAtom } from '@jotai/atoms';
import useAdminWorkspace from '@hooks/admin/useAdminWorkspace';
import NewCommonButton from '@components/common/button/NewCommonButton';
import OnboardingActionHighlight from '@components/admin/order/table-manage/common/OnboardingActionHighlight';
import { ONBOARDING_MIN_TABLE_COUNT } from '@components/admin/workspace/onboarding/onboardingData';
import SettingSection from './SettingSection';
import { colFlex } from '@styles/flexStyles';
import { MAX_TABLE_COUNT } from '@constants/layout';
import { getAdminWorkspacePath } from '@constants/routes';
import { getTableStatus, TABLE_STATUS } from '@utils/tableStatus';
import TableTimeSetting from './TableTimeSetting';
import TableQRDownload from './TableQRDownload';
import NumberInput from '@components/common/input/NumberInput';
import { Color } from '@resources/colors';

const Container = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 20px 0 24px 0;
  gap: 16px;
  ${colFlex({ justify: 'start', align: 'start' })};
`;

const CountCaption = styled.div`
  font-size: 11px;
  color: ${Color.MUTED_GREY};
`;

const SaveHighlight = styled(OnboardingActionHighlight)`
  width: 100%;
  margin-top: auto;
`;

const SaveButton = styled(NewCommonButton)`
  width: 100%;
`;

function TableSettingsSidebar() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const workspace = useAtomValue(adminWorkspaceAtom);
  const tables = useAtomValue(adminTablesAtom);
  const setExternalSidebar = useSetAtom(externalSidebarAtom);
  const { updateWorkspaceTableCount, updateWorkspaceOrderSetting } = useAdminWorkspace();
  const navigate = useNavigate();

  const [tableCount, setTableCount] = useState(workspace.tableCount || 1);
  const [isTimeLimited, setIsTimeLimited] = useState(workspace.workspaceSetting?.useOrderSessionTimeLimit ?? false);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(workspace.workspaceSetting?.orderSessionTimeLimitMinutes ?? 60);

  const isDirty =
    tableCount !== workspace.tableCount ||
    isTimeLimited !== workspace.workspaceSetting?.useOrderSessionTimeLimit ||
    timeLimitMinutes !== workspace.workspaceSetting?.orderSessionTimeLimitMinutes;

  // 온보딩 테이블 단계는 2개 이상이어야 완료된다 — 개수를 채울 때까지 입력을, 채운 뒤엔 적용 버튼을 유도한다
  const needsTableCountOnboarding = workspace.isOnboarding && workspace.tableCount < ONBOARDING_MIN_TABLE_COUNT;
  const highlightTableCount = needsTableCountOnboarding && tableCount < ONBOARDING_MIN_TABLE_COUNT;
  const highlightSave = needsTableCountOnboarding && tableCount >= ONBOARDING_MIN_TABLE_COUNT;

  const handleTableCountMinus = () => {
    setTableCount(Math.max(1, tableCount - 1));
  };

  const handleTableCountPlus = () => {
    setTableCount(Math.min(MAX_TABLE_COUNT, tableCount + 1));
  };

  const handleTableCountChange = (value: number) => {
    setTableCount(Math.min(MAX_TABLE_COUNT, Math.max(1, value)));
  };

  const getUsingTableNumbersOverCount = () =>
    tables.filter((table) => table.tableNumber > tableCount && getTableStatus(table) !== TABLE_STATUS.EMPTY).map((table) => table.tableNumber);

  const handleSave = async () => {
    if (tableCount < 1) {
      alert('테이블 수는 1 이상이어야 합니다.');
      return;
    }

    if (isTimeLimited && (timeLimitMinutes === undefined || timeLimitMinutes < 1)) {
      alert('시간 제한은 1분 이상이어야 합니다.');
      return;
    }

    const usingOverCount = getUsingTableNumbersOverCount();
    if (usingOverCount.length > 0) {
      const confirmed = window.confirm(`${usingOverCount.join(', ')}번 테이블이 사용 중입니다.\n계속하면 이 테이블들이 화면에서 사라집니다. 진행할까요?`);
      if (!confirmed) return;
    }

    const hasPlacedRemainingTable = tables.some((table) => table.tableNumber <= tableCount && table.position != null);
    const shouldContinueTableOnboarding = workspace.isOnboarding && tableCount >= ONBOARDING_MIN_TABLE_COUNT;
    const shouldReturnToOnboarding = shouldContinueTableOnboarding && hasPlacedRemainingTable;

    try {
      await Promise.all([updateWorkspaceTableCount(workspaceId, tableCount), updateWorkspaceOrderSetting(workspaceId, isTimeLimited, timeLimitMinutes)]);

      if (shouldReturnToOnboarding) {
        navigate(getAdminWorkspacePath(workspace.id));
        return;
      }

      if (shouldContinueTableOnboarding) {
        setExternalSidebar({ action: RIGHT_SIDEBAR_ACTION.CLOSE });
      }
    } catch (error) {
      console.error('설정 저장 실패:', error);
      alert('설정 저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <Container>
      <TableQRDownload workspaceId={workspaceId} workspaceName={workspace.name} tables={tables} />

      <SettingSection label="테이블 개수">
        <OnboardingActionHighlight active={highlightTableCount}>
          <NumberInput
            value={tableCount}
            formatter={(v) => `${v}개`}
            maxWidth="100%"
            onChange={handleTableCountChange}
            onIncrement={handleTableCountPlus}
            onDecrement={handleTableCountMinus}
          />
        </OnboardingActionHighlight>
        <CountCaption>최대 {MAX_TABLE_COUNT}개까지 만들 수 있어요</CountCaption>
      </SettingSection>

      <TableTimeSetting
        isTimeLimited={isTimeLimited}
        timeLimitMinutes={timeLimitMinutes}
        onTimeLimitedChange={setIsTimeLimited}
        onMinutesChange={setTimeLimitMinutes}
      />

      <SaveHighlight active={highlightSave}>
        <SaveButton size={'xs'} onClick={handleSave} disabled={!isDirty}>
          적용
        </SaveButton>
      </SaveHighlight>
    </Container>
  );
}

export default TableSettingsSidebar;
